import React, { useMemo } from "react";
import EChart from "./EChart";
import { ECHART_TOOLTIP, ECHART_TOOLBOX, ECHART_PALETTE } from "../../Utilities/echartsTheme";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";
import moment from "moment";

// Stable, semantic colors per payment method so the same method always
// gets the same color across renders and views.
const PAYMENT_METHOD_COLORS = {
  Cash: "#10B981",
  Card: "#3B82F6",
  "Bank Transfer": "#D97706",
  COD: "#10B981",
  Bank: "#D97706",
};

const colorForMethod = (method, idx) =>
  PAYMENT_METHOD_COLORS[method] || ECHART_PALETTE[idx % ECHART_PALETTE.length];

// Stacked column chart comparing order counts by payment method over time.
// Vertical bars per day, segments stacked by payment method — shows both the
// total daily volume and the per-method composition with precise values.
// data: [{ date: 'YYYY-MM-DD', payment_method: 'Cash', order_count: N, revenue: R }, ...]
const EChartsPaymentMethodsLine = ({ data }) => {
  const hasData = Array.isArray(data) && data.length > 0;

  const { dates, methods, seriesByMethod, totalsByDate } = useMemo(() => {
    if (!hasData) return { dates: [], methods: [], seriesByMethod: {}, totalsByDate: [] };

    const start = moment.min(data.map((d) => moment(d.date)));
    const end = moment.max(data.map((d) => moment(d.date)));

    // Build a map: dateKey -> { method -> count }
    const byDate = {};
    const methodSet = new Set();
    data.forEach((row) => {
      const key = moment(row.date).format("YYYY-MM-DD");
      const method = row.payment_method || "Unknown";
      methodSet.add(method);
      if (!byDate[key]) byDate[key] = {};
      byDate[key][method] = Number(row.order_count) || 0;
    });

    const dateList = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      dateList.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }

    const methodsArr = [...methodSet].sort();
    const map = {};
    methodsArr.forEach((method) => {
      map[method] = dateList.map((key) => byDate[key]?.[method] || 0);
    });

    return { dates: dateList, methods: methodsArr, seriesByMethod: map };
  }, [data, hasData]);

  const option = useMemo(() => {
    if (!hasData) return {};

    return {
      tooltip: {
        ...ECHART_TOOLTIP,
        trigger: "axis",
        formatter: (params) => {
          const date = moment(params[0].axisValue).format("DD MMM YYYY");
          let html = `<div style="font-weight:600;margin-bottom:4px">${date}</div>`;
          let total = 0;
          // Show methods in stack order (reverse so top-of-stack appears first)
          params.forEach((p) => {
            total += p.value;
            html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
              ${p.seriesName}: <strong>${formatRawNumber(p.value)}</strong>
            </div>`;
          });
          html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid #E2E8F0;font-weight:600">Total: ${formatRawNumber(total)}</div>`;
          return html;
        },
      },
      legend: {
        top: 0,
        right: 50,
        textStyle: { fontSize: 12, color: "#64748B" },
        itemWidth: 16,
        itemHeight: 10,
        icon: "roundRect",
      },
      toolbox: {
        ...ECHART_TOOLBOX,
        feature: {
          ...ECHART_TOOLBOX.feature,
          dataZoom: { title: { zoom: "Zoom", back: "Reset" } },
        },
      },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: false,
        axisLabel: {
          formatter: (v) => moment(v).format("DD MMM"),
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        name: "Orders",
        nameTextStyle: { color: "#64748B", fontSize: 11 },
        minInterval: 1,
        axisLabel: { formatter: (v) => formatNumber(v), color: "#64748B" },
        splitLine: { lineStyle: { color: "#F1F5F9" } },
      },
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        {
          type: "slider",
          start: 0,
          end: 100,
          height: 20,
          bottom: 5,
          borderColor: "#E2E8F0",
          fillerColor: "rgba(59,130,246,0.1)",
          handleStyle: { color: "#3B82F6" },
          moveHandleSize: 7,
        },
      ],
      series: methods.map((method, idx) => {
        const color = colorForMethod(method, idx);
        return {
          name: method,
          type: "bar",
          stack: "paymentMethods",
          data: seriesByMethod[method],
          barWidth: "60%",
          itemStyle: {
            color,
            borderRadius: idx === methods.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0],
          },
          emphasis: { focus: "series" },
          animationDuration: 600,
          animationDelay: idx * 100,
        };
      }),
    };
  }, [dates, methods, seriesByMethod, hasData]);

  if (!hasData) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        No data for the selected period
      </div>
    );
  }

  return <EChart option={option} />;
};

export default EChartsPaymentMethodsLine;
